import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { AppointmentsCalendarConfigResourceType } from '@prisma/client';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { trpc } from '~/api/trpc';
import { toast } from 'sonner';

type ResourceItem = {
    id: string;
    type: AppointmentsCalendarConfigResourceType;
    title: string;
    orderIdx: number;
    practitionerId?: number | null;
    roomId?: number | null;
    deviceId?: number | null;
    configResourceId?: string; // For existing resources
};

type ResourceOrderDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storeId: number;
};

type ResourceItemComponentProps = {
    resource: ResourceItem;
    index: number;
    totalItems: number;
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const ResourceItemComponent: React.FC<ResourceItemComponentProps> = ({
    resource,
    index,
    totalItems,
    onMoveUp,
    onMoveDown,
}) => {
    const canMoveUp = index > 0;
    const canMoveDown = index < totalItems - 1;

    return (
        <div className="flex items-center p-3 bg-white border border-gray-200 rounded mb-2">
            <div className="flex flex-col mr-3">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 p-0"
                    onClick={onMoveUp}
                    disabled={!canMoveUp}
                    aria-label={`Move ${resource.title} up`}
                >
                    <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 p-0"
                    onClick={onMoveDown}
                    disabled={!canMoveDown}
                    aria-label={`Move ${resource.title} down`}
                >
                    <ArrowDown className="h-3 w-3" />
                </Button>
            </div>
            <span className="text-sm font-medium flex-1">{resource.title}</span>
        </div>
    );
};

export const ResourceOrderDialog: React.FC<ResourceOrderDialogProps> = ({
    open,
    onOpenChange,
    storeId,
}) => {
    const trpcUtils = trpc.useUtils();
    const [orderedResources, setOrderedResources] = useState<ResourceItem[]>([]);
    const [isSaving, setIsSaving] = useState(false);


    // Fetch config for the store
    const { data: configData, isLoading: isLoadingConfig } = trpc.appointmentsCalendarConfig.getByStoreId.useQuery(
        { storeId },
        {
            enabled: open && !!storeId,
            refetchOnWindowFocus: false,
        }
    );

    // Fetch resources data
    const { data: resourcesData } = trpc.appointments.getResources.useQuery(undefined, {
        enabled: open,
        refetchOnWindowFocus: false,
    });

    // Initialize resources when config or resources data changes
    useEffect(() => {
        if (!open || !resourcesData) return;

        const config = configData?.data;
        const configResources = config?.resources || [];

        // Build resource items from config if it exists
        if (configResources.length > 0) {
            const items: ResourceItem[] = configResources.map((res) => {
                let title = '';
                let id = '';

                switch (res.type) {
                    case 'NOT_ASSIGNED':
                        title = 'Nicht zugewiesen';
                        id = 'unassigned';
                        break;
                    case 'PROCESSING_TIME':
                        title = 'Einwirkzeit / Durchlaufzeit';
                        id = 'waiting';
                        break;
                    case 'RECEPTION':
                        title = 'Empfang';
                        id = 'reception';
                        break;
                    case 'PRACTITIONER':
                        if (res.practitioner) {
                            title = `${res.practitioner.firstName} ${res.practitioner.lastName}`;
                            id = `practitioner-${res.practitioner.id}`;
                        }
                        break;
                    case 'ROOM':
                        if (res.room) {
                            title = res.room.name;
                            id = `room-${res.room.id}`;
                        }
                        break;
                    case 'DEVICE':
                        if (res.device) {
                            title = res.device.name;
                            id = `equipment-${res.device.id}`;
                        }
                        break;
                }

                return {
                    id,
                    type: res.type,
                    title,
                    orderIdx: res.orderIdx,
                    practitionerId: res.practitionerId,
                    roomId: res.roomId,
                    deviceId: res.deviceId,
                    configResourceId: res.id,
                };
            }).filter((item) => item.title && item.id);

            // Add any missing resources that exist in resourcesData but not in config
            const existingIds = new Set(items.map((item) => item.id));

            // Add missing special columns
            if (!existingIds.has('unassigned')) {
                items.push({
                    id: 'unassigned',
                    type: 'NOT_ASSIGNED',
                    title: 'Nicht zugewiesen',
                    orderIdx: items.length,
                });
            }
            if (!existingIds.has('waiting')) {
                items.push({
                    id: 'waiting',
                    type: 'PROCESSING_TIME',
                    title: 'Einwirkzeit / Durchlaufzeit',
                    orderIdx: items.length,
                });
            }
            if (!existingIds.has('reception')) {
                items.push({
                    id: 'reception',
                    type: 'RECEPTION',
                    title: 'Empfang',
                    orderIdx: items.length,
                });
            }

            // Add missing practitioners
            const storePractitioners = (resourcesData.practitioners || []).filter((p) => {
                const storeIds = p.practitioner_store_practitioner_store_practitionerTopractitioner.map(
                    (assoc) => assoc.store_practitioner_store_storeTostore.id
                );
                return storeIds.includes(storeId);
            });

            storePractitioners.forEach((practitioner) => {
                const id = `practitioner-${practitioner.id}`;
                if (!existingIds.has(id)) {
                    items.push({
                        id,
                        type: 'PRACTITIONER',
                        title: `${practitioner.firstName} ${practitioner.lastName}`,
                        orderIdx: items.length,
                        practitionerId: practitioner.id,
                    });
                }
            });

            // Add missing rooms
            const storeRooms = (resourcesData.rooms || []).filter((r) => r.storeId === storeId);
            storeRooms.forEach((room) => {
                const id = `room-${room.id}`;
                if (!existingIds.has(id)) {
                    items.push({
                        id,
                        type: 'ROOM',
                        title: room.name,
                        orderIdx: items.length,
                        roomId: room.id,
                    });
                }
            });

            // Add missing devices
            const storeEquipment = (resourcesData.equipment || []).filter((e) => e.storeId === storeId);
            storeEquipment.forEach((equipment) => {
                const id = `equipment-${equipment.id}`;
                if (!existingIds.has(id)) {
                    items.push({
                        id,
                        type: 'DEVICE',
                        title: equipment.name,
                        orderIdx: items.length,
                        deviceId: equipment.id,
                    });
                }
            });

            setOrderedResources(items.sort((a, b) => a.orderIdx - b.orderIdx));
        } else {
            // No config exists, create default order
            const items: ResourceItem[] = [];

            // Special columns first
            items.push({
                id: 'unassigned',
                type: 'NOT_ASSIGNED',
                title: 'Nicht zugewiesen',
                orderIdx: 0,
            });
            items.push({
                id: 'waiting',
                type: 'PROCESSING_TIME',
                title: 'Einwirkzeit / Durchlaufzeit',
                orderIdx: 1,
            });
            items.push({
                id: 'reception',
                type: 'RECEPTION',
                title: 'Empfang',
                orderIdx: 2,
            });

            // Then practitioners (alphabetically)
            const storePractitioners = (resourcesData.practitioners || []).filter((p) => {
                const storeIds = p.practitioner_store_practitioner_store_practitionerTopractitioner.map(
                    (assoc) => assoc.store_practitioner_store_storeTostore.id
                );
                return storeIds.includes(storeId);
            }).sort((a, b) => {
                const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
                const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });

            storePractitioners.forEach((practitioner, idx) => {
                items.push({
                    id: `practitioner-${practitioner.id}`,
                    type: 'PRACTITIONER',
                    title: `${practitioner.firstName} ${practitioner.lastName}`,
                    orderIdx: items.length,
                    practitionerId: practitioner.id,
                });
            });

            // Then rooms (alphabetically)
            const storeRooms = (resourcesData.rooms || []).filter((r) => r.storeId === storeId)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

            storeRooms.forEach((room) => {
                items.push({
                    id: `room-${room.id}`,
                    type: 'ROOM',
                    title: room.name,
                    orderIdx: items.length,
                    roomId: room.id,
                });
            });

            // Then devices (alphabetically)
            const storeEquipment = (resourcesData.equipment || []).filter((e) => e.storeId === storeId)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

            storeEquipment.forEach((equipment) => {
                items.push({
                    id: `equipment-${equipment.id}`,
                    type: 'DEVICE',
                    title: equipment.name,
                    orderIdx: items.length,
                    deviceId: equipment.id,
                });
            });

            setOrderedResources(items);
        }
    }, [configData, resourcesData, storeId, open]);

    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        
        const newResources = [...orderedResources];
        const temp = newResources[index];
        newResources[index] = newResources[index - 1];
        newResources[index - 1] = temp;
        
        // Update orderIdx for all items
        const updated = newResources.map((item, idx) => ({
            ...item,
            orderIdx: idx,
        }));
        setOrderedResources(updated);
    };

    const handleMoveDown = (index: number) => {
        if (index === orderedResources.length - 1) return;
        
        const newResources = [...orderedResources];
        const temp = newResources[index];
        newResources[index] = newResources[index + 1];
        newResources[index + 1] = temp;
        
        // Update orderIdx for all items
        const updated = newResources.map((item, idx) => ({
            ...item,
            orderIdx: idx,
        }));
        setOrderedResources(updated);
    };

    const upsertConfigMutation = trpc.appointmentsCalendarConfig.upsertConfig.useMutation();
    const setResourcesMutation = trpc.appointmentsCalendarConfig.setResources.useMutation({
        onSuccess: () => {
            toast.success('Spaltenreihenfolge gespeichert');
            trpcUtils.appointments.getResources.invalidate();
            trpcUtils.appointmentsCalendarConfig.getByStoreId.invalidate({ storeId });
            onOpenChange(false);
            setIsSaving(false);
        },
        onError: (error) => {
            toast.error(`Fehler beim Speichern: ${error.message}`);
            setIsSaving(false);
        },
    });

    const handleSave = async () => {
        if (orderedResources.length === 0) {
            toast.error('Keine Ressourcen zum Speichern');
            return;
        }

        setIsSaving(true);

        try {
            // Ensure config exists
            if (!configData?.data) {
                await upsertConfigMutation.mutateAsync({ storeId });
            }

            // Convert to setResources format
            const resourcesInput = orderedResources.map((item) => ({
                type: item.type,
                orderIdx: item.orderIdx,
                practitionerId: item.practitionerId ?? null,
                roomId: item.roomId ?? null,
                deviceId: item.deviceId ?? null,
            }));

            await setResourcesMutation.mutateAsync({
                storeId,
                resources: resourcesInput,
            });
        } catch (error) {
            console.error('Error saving resource order:', error);
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Spaltenreihenfolge bearbeiten</DialogTitle>
                    <DialogDescription>
                        Verwenden Sie die Pfeiltasten, um die Reihenfolge der Spalten im Kalender zu ändern.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-4">
                    {isLoadingConfig ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : orderedResources.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">
                            Keine Ressourcen verfügbar.
                        </p>
                    ) : (
                        <div>
                            {orderedResources.map((resource, index) => (
                                <ResourceItemComponent
                                    key={resource.id}
                                    resource={resource}
                                    index={index}
                                    totalItems={orderedResources.length}
                                    onMoveUp={() => handleMoveUp(index)}
                                    onMoveDown={() => handleMoveDown(index)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving || orderedResources.length === 0}>
                        {isSaving ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Speichern...
                            </>
                        ) : (
                            'Speichern'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
